// React Query hooks for the tracking system. Provides per-contract
// lists (paginated), single-item fetches, create/update/archive mutations,
// recurrence generation, per-contract tab badge counts, parent-filtered child
// queries (KPI measurements/reports), org-wide lists, dashboard summary, and
// linked-deliverable queries + the milestone↔deliverables bulk-set mutation.
// Exports `buildTrackingDeepLink` — the single source of the deep-link URL
// schema shared by the email notifications and the tab route parser.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trackingApi } from "../services/api";
import { tabSlugForType } from "../components/manage/tabs/tabRegistry";
import { trackingRetry } from "./trackingRetry";
import { trackingKeys } from "./trackingKeys";
import type {
  TrackingType,
  TabLevelType,
  TrackingListParams,
  CreateTrackingInput,
  UpdateTrackingInput,
  TrackingItem,
  TrackingItemListResponse,
  Deliverable,
  KpiBatchRequest,
  KpiBatchResponse,
  RejectVariationInput,
  Variation,
  Sow,
  CorrectOriginalValueInput,
  CorrectStatusInput,
  CorrectVariationInput,
  Issue,
  IssueThreadEntry,
  TransitionIssueInput,
  CloseIssueInput,
  AppendThreadCommentInput,
  AppendThreadActionInput,
  MarkActionDoneInput,
} from "../types";

// ─── Deep-link helper ──────────────────────────────────────────────────
// Single URL schema source — consumed by the email notification builder
// and the tab route parser. Future child-link extension (e.g. `?child=...`)
// is non-breaking additive.
//
// Routes the type through tabSlugForType() so 'sow' and 'variation' both
// produce ?tab=variations-sows — the only valid UI slug for those types.

/** Deep-link URL schema — the canonical tracking item URL shape. */
export const TRACKING_DEEP_LINK_PATH =
  "/manage/contracts/:contractId?tab=:type&item=:itemId";

export function buildTrackingDeepLink(
  contractId: string,
  type: TabLevelType,
  itemId: string,
): string {
  const params = new URLSearchParams({
    tab: tabSlugForType(type),
    item: itemId,
  });
  return `/manage/contracts/${encodeURIComponent(contractId)}?${params.toString()}`;
}

// ─── Retry helper ─────────────────────────────────────────────────────
// The retry policy lives in its own leaf module (./trackingRetry) so that
// explicit-factory mocks of this module don't have to re-declare it. Re-
// exported here for existing importers.
export { trackingRetry } from "./trackingRetry";

// ─── Queries ───────────────────────────────────────────────────────────

export function useTrackingItems(
  contractId: string,
  type: TrackingType,
  params?: TrackingListParams,
  options?: { enabled?: boolean },
) {
  const { includeArchived, ...rest } = params ?? {};
  const explicitlyDisabled = options?.enabled === false;
  return useQuery<TrackingItemListResponse>({
    queryKey: trackingKeys.list(contractId, type, !!includeArchived, rest),
    queryFn: () => trackingApi.list(contractId, type, params),
    enabled: !!contractId && !!type && !explicitlyDisabled,
    // Mutations push-invalidate every tracking key via
    // invalidateForContractType, so a non-zero staleTime only suppresses
    // redundant refetches on remount (row expanders mount fresh hook
    // instances per row). Same rationale for the other 30s staleTimes in
    // this module's drain hooks.
    staleTime: 30_000,
    retry: trackingRetry,
  });
}

// Contract-wide deliverable list for the summary-strip tiles AND the
// post-B1 deliverable table. Loops pages via `nextToken` until
// exhausted so the Overdue / Due this week / Awaiting review tiles
// remain accurate on contracts that exceed a single page; a 50-
// iteration safety cap protects against a misbehaving backend
// returning a non-progressing token. Consumers read `data` as a flat
// array (defaulting to `[]` during loading).
//
// Fetches with `includeArchived: true` so the Include-archived chip
// in DeliverableTab can toggle visibility client-side off the same
// cached set instead of round-tripping the API. The summary tile
// counts already filter archived rows out of their predicates by
// other means (status / dueDate), so an archived row never inflates
// a tile.
export function useAllDeliverablesForSummary(contractId: string): {
  data: Deliverable[];
  isPending: boolean;
  error: unknown;
} {
  const query = useQuery<Deliverable[]>({
    queryKey: trackingKeys.summaryAll(contractId),
    queryFn: async () => {
      const accumulated: Deliverable[] = [];
      let nextToken: string | undefined;
      let iterations = 0;
      do {
        if (iterations++ >= MAX_PAGE_ITERATIONS) {
          console.error(
            "useAllDeliverablesForSummary: max iterations exceeded (backend nextToken not progressing)",
            { contractId },
          );
          break;
        }
        const page = await trackingApi.list(contractId, "deliverable", {
          limit: 200,
          nextToken,
          includeArchived: true,
        });
        for (const item of page.items) {
          if (item.type === "deliverable") {
            accumulated.push(item as Deliverable);
          }
        }
        nextToken = page.nextToken;
      } while (nextToken);
      return accumulated;
    },
    enabled: !!contractId,
    staleTime: 30_000,
    retry: trackingRetry,
  });
  return {
    data: query.data ?? [],
    isPending: query.isPending,
    error: query.error,
  };
}

// Contract-wide issue list for back-link surfaces (the open-issue count
// badge on deliverable / milestone / KPI rows). Pages through `nextToken`
// to its safety cap so the count reflects the full register, not just the
// first page — a busy contract with mixed tracking rows can easily push
// issues past one page of GSI1 results.
//
// Cached separately from `useTrackingItems(contractId, "issue")` (the
// Issues tab's list query) so each consumer stays in control of its own
// pagination strategy and the badge never accidentally consumes a
// single-page result that another component happens to have fetched.
export function useAllIssuesForBackLinkCount(contractId: string): {
  data: Issue[];
  isPending: boolean;
  error: unknown;
} {
  const query = useQuery<Issue[]>({
    queryKey: trackingKeys.backlinkAll(contractId),
    queryFn: async () => {
      const accumulated: Issue[] = [];
      let nextToken: string | undefined;
      let iterations = 0;
      do {
        if (iterations++ >= MAX_PAGE_ITERATIONS) {
          console.error(
            "useAllIssuesForBackLinkCount: max iterations exceeded (backend nextToken not progressing)",
            { contractId },
          );
          break;
        }
        const page = await trackingApi.list(contractId, "issue", {
          limit: 200,
          nextToken,
        });
        for (const item of page.items) {
          if (item.type === "issue") accumulated.push(item as Issue);
        }
        nextToken = page.nextToken;
      } while (nextToken);
      return accumulated;
    },
    enabled: !!contractId,
    staleTime: 30_000,
    retry: trackingRetry,
  });
  return {
    data: query.data ?? [],
    isPending: query.isPending,
    error: query.error,
  };
}

export function useTrackingItem(
  contractId: string,
  type: TrackingType,
  itemId: string,
) {
  return useQuery<TrackingItem>({
    queryKey: trackingKeys.item(contractId, type, itemId),
    queryFn: () => trackingApi.get(contractId, type, itemId),
    enabled: !!contractId && !!type && !!itemId,
    staleTime: 0,
    retry: trackingRetry,
  });
}

// ─── useLinkedDeliverables ─────────────────────────────────────────────
// Fetches deliverables whose milestoneId FK points at the given milestone —
// the single canonical link between a milestone and its deliverables.
export function useLinkedDeliverables(
  contractId: string,
  milestoneId: string,
) {
  return useQuery<Deliverable[]>({
    queryKey: trackingKeys.linkedDeliverables(contractId, milestoneId),
    queryFn: async () => {
      const resp = await trackingApi.linkedDeliverables(contractId, milestoneId);
      return resp.items.filter((i): i is Deliverable => i.type === "deliverable");
    },
    enabled: !!contractId && !!milestoneId,
    staleTime: 30_000,
    retry: trackingRetry,
  });
}

// ─── useSetLinkedDeliverables ─────────────────────────────────────────
// Idempotent bulk-set mutation for the milestone↔deliverables FK link.
// Dispatched by MilestoneDeliverablesPicker when the user clicks Save on
// the milestone edit form: the picker sends the full ticked set, and the
// backend computes the additions/removals against the current FK state.
// On success, invalidates BOTH the deliverable list and the linked-
// deliverables query so the picker and the read-only section reflect the
// new state.
export function useSetLinkedDeliverables() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      milestoneId,
      deliverableIds,
    }: {
      contractId: string;
      milestoneId: string;
      deliverableIds: string[];
    }) =>
      trackingApi.setLinkedDeliverables(contractId, milestoneId, deliverableIds),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: trackingKeys.linkedDeliverables(
          vars.contractId,
          vars.milestoneId,
        ),
      });
      // FK lives on the deliverable row, so the deliverable list cache
      // must also refresh — otherwise the picker would render stale
      // tick state on a subsequent open.
      invalidateForContractType(queryClient, vars.contractId, "deliverable");
    },
  });
}

export function useContractTrackingCounts(contractId: string) {
  return useQuery({
    queryKey: trackingKeys.counts(contractId),
    queryFn: () => trackingApi.counts(contractId),
    enabled: !!contractId,
    staleTime: 30_000,
    retry: trackingRetry,
  });
}

// ─── useTrackingItemsByParent ─────────────────────────────────────────
// Fetches all pages from the list endpoint and filters by parentId client-side.
// Bounded by a 500-item ceiling — beyond that, logs a warning and returns the
// truncated set. `console.warn` fires once per queryFn execution (not per
// hook render); React Query caches the result so re-renders do not re-warn.
// `refetch()` or cache invalidation WILL re-fire the warn — acceptable
// because ceiling hits are rare.
//
// If a single parent exceeds the 500-child ceiling, migrate to a
// server-side `?parentId=` filter rather than client-side pagination.

const MAX_ITEMS_BY_PARENT = 500;
/** Defence-in-depth against a buggy backend that returns a repeated/non-progressing nextToken. */
const MAX_PAGE_ITERATIONS = 50;

interface ParentFilteredResult {
  items: TrackingItem[];
  /** True when the 500-item ceiling was hit before reading all pages. */
  truncated: boolean;
}

/**
 * NOTE: pagination is NOT surfaced. The hook internally pages through results,
 * filters by parentId, and returns the accumulated set (bounded by a 500-item
 * ceiling with truncated=true signal). Consumers do NOT pass limit/nextToken.
 * For paginated iteration, use useTrackingItems with explicit paging.
 */
export function useTrackingItemsByParent(
  contractId: string,
  type: TrackingType,
  parentId: string,
) {
  return useQuery<ParentFilteredResult>({
    queryKey: trackingKeys.byParent(contractId, type, parentId),
    enabled: !!contractId && !!type && !!parentId,
    staleTime: 0,
    retry: trackingRetry,
    queryFn: async () => {
      const accumulated: TrackingItem[] = [];
      let nextToken: string | undefined;
      let truncated = false;
      let iterations = 0;

      do {
        if (iterations++ >= MAX_PAGE_ITERATIONS) {
          console.error(
            "useTrackingItemsByParent: max iterations exceeded (backend nextToken not progressing)",
            { contractId, parentId, type },
          );
          truncated = true;
          break;
        }
        const page = await trackingApi.list(contractId, type, {
          limit: 100,
          nextToken,
        });
        for (const item of page.items) {
          if (item.parentId === parentId) {
            accumulated.push(item);
            if (accumulated.length >= MAX_ITEMS_BY_PARENT) {
              truncated = true;
              break;
            }
          }
        }
        if (truncated) break;
        nextToken = page.nextToken;
      } while (nextToken);

      if (truncated) {
        console.warn(
          "useTrackingItemsByParent: hit 500-item ceiling; migrate to server-side ?parentId=",
          { contractId, parentId, type },
        );
      }

      return { items: accumulated, truncated };
    },
  });
}

export function useOrgWideTracking(
  type: TrackingType,
  params?: TrackingListParams,
) {
  return useQuery<TrackingItemListResponse>({
    queryKey: trackingKeys.orgWide(type, params ?? {}),
    queryFn: () => trackingApi.orgWideList(type, params),
    enabled: !!type,
    staleTime: 0,
    retry: trackingRetry,
  });
}

/**
 * Dry-run preview of the generate-occurrences endpoint. Consumes the
 * backend's `{count, occurrenceDates, capped}` response so the Generate
 * Occurrences dialog can show an exact forecast before the user confirms.
 * The recurrenceRule snapshot is folded into the query key so preview
 * refetches whenever the rule mutates between opens of the dialog.
 *
 * Retry semantics inherit `trackingRetry` — 4xx surfaces cleanly (e.g.
 * 400 "not recurrence-capable" when the caller opens the dialog on a
 * non-recurring item), 5xx retries up to 3.
 */
export function usePreviewOccurrences(
  contractId: string,
  type: TrackingType,
  itemId: string,
  /** Serialisable snapshot of the item's recurrenceRule — used as cache key. */
  recurrenceKey?: unknown,
  enabled = true,
) {
  return useQuery({
    queryKey: trackingKeys.previewOccurrences(
      contractId,
      type,
      itemId,
      recurrenceKey ?? null,
    ),
    queryFn: () => trackingApi.previewOccurrences(contractId, type, itemId),
    enabled: enabled && !!contractId && !!type && !!itemId,
    staleTime: 0,
    retry: trackingRetry,
  });
}

export function useTrackingSummary() {
  return useQuery({
    queryKey: trackingKeys.summary,
    queryFn: () => trackingApi.summary(),
    staleTime: 60_000,
    retry: trackingRetry,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────

interface CreateTrackingVariables {
  contractId: string;
  type: TrackingType;
  input: CreateTrackingInput;
  /** Required when type is kpi-measurement or kpi-report — used to invalidate the parent KPI cache. */
  parentId?: string;
}

function invalidateForContractType(
  queryClient: ReturnType<typeof useQueryClient>,
  contractId: string,
  type: TrackingType,
  options: { parentId?: string; invalidateActivity?: boolean } = {},
) {
  const { parentId, invalidateActivity = true } = options;
  queryClient.invalidateQueries({
    queryKey: trackingKeys.type(contractId, type),
  });
  queryClient.invalidateQueries({
    queryKey: trackingKeys.counts(contractId),
  });
  queryClient.invalidateQueries({ queryKey: trackingKeys.summary });
  queryClient.invalidateQueries({
    queryKey: trackingKeys.orgWideType(type),
  });
  // Child types also invalidate the parent KPI's item + list so the
  // Reporting view surfaces last-measurement / last-report data.
  if (
    (type === "kpi-measurement" || type === "kpi-report") &&
    parentId
  ) {
    queryClient.invalidateQueries({
      queryKey: trackingKeys.item(contractId, "kpi", parentId),
    });
    queryClient.invalidateQueries({
      queryKey: trackingKeys.type(contractId, "kpi"),
    });
  }
  // The milestone drawer's linked-deliverables caches key under the
  // milestone subtree, but the FK lives on the deliverable row — a
  // deliverable edit can re-point milestoneId (or archive the row), and
  // the mutation doesn't know which milestones are affected, so refresh
  // them all for the contract.
  if (type === "deliverable") {
    queryClient.invalidateQueries({
      predicate: (q) =>
        trackingKeys.isLinkedDeliverablesKey(contractId)(q.queryKey),
    });
  }
  // Prefix-invalidates ["activity", contractId, ...] so the recent-entries
  // and drawer activity feeds refresh after any tracking mutation.
  if (invalidateActivity) {
    queryClient.invalidateQueries({ queryKey: ["activity", contractId] });
  }
}

export function useCreateTrackingItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, type, input }: CreateTrackingVariables) =>
      trackingApi.create(contractId, type, input),
    onSuccess: (_data, vars) => {
      invalidateForContractType(
        queryClient,
        vars.contractId,
        vars.type,
        { parentId: vars.parentId },
      );
    },
  });
}

interface UpdateTrackingVariables {
  contractId: string;
  type: TrackingType;
  itemId: string;
  input: UpdateTrackingInput;
  parentId?: string;
}

export function useUpdateTrackingItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, type, itemId, input }: UpdateTrackingVariables) =>
      trackingApi.update(contractId, type, itemId, input),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: trackingKeys.item(vars.contractId, vars.type, vars.itemId),
      });
      invalidateForContractType(
        queryClient,
        vars.contractId,
        vars.type,
        { parentId: vars.parentId },
      );
    },
  });
}

interface ArchiveTrackingVariables {
  contractId: string;
  type: TrackingType;
  itemId: string;
  reason: string;
  parentId?: string;
}

export function useArchiveTrackingItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      type,
      itemId,
      reason,
    }: ArchiveTrackingVariables) =>
      trackingApi.archive(contractId, type, itemId, reason),
    onSuccess: (_data, vars) => {
      invalidateForContractType(
        queryClient,
        vars.contractId,
        vars.type,
        { parentId: vars.parentId },
      );
    },
  });
}

interface GenerateOccurrencesVariables {
  contractId: string;
  type: TrackingType;
  itemId: string;
}

export function useGenerateOccurrences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, type, itemId }: GenerateOccurrencesVariables) =>
      trackingApi.generateOccurrences(contractId, type, itemId),
    onSuccess: (_data, vars) => {
      invalidateForContractType(queryClient, vars.contractId, vars.type);
    },
  });
}

// Bulk-log the same period across many KPIs in one round-trip via
// POST /kpi-period-batch. Partial success is the contract: the
// mutation resolves with the full `KpiBatchResponse` so callers can
// inspect per-row `ok` flags — throwing only on transport / 4xx
// failures surfaced by apiRequest. On any successful writes we
// invalidate the KPI + child caches for the contract so the drawer /
// overview trend pick up fresh data.
//
// Variables shape is `{ contractId, items }` (flattened) — callers
// construct batch items directly rather than nesting under `body`.
interface BulkCreateKpiPeriodsVariables extends KpiBatchRequest {
  contractId: string;
}

// ─── Variation approve / reject hooks ─────────────────────────────────
// Status transitions for a variation go through dedicated endpoints, not
// the standard PUT path — the backend `UpdateVariationSchema` rejects a
// `status` field on PUT. These hooks call POST .../approve and
// .../reject; the wire body for approve is empty, for reject it carries
// an optional reason. Both invalidate the variation list + specific item
// + counts + summary so the UI catches up immediately.

interface ApproveVariationVariables {
  contractId: string;
  itemId: string;
}

export function useApproveVariation() {
  const queryClient = useQueryClient();

  return useMutation<Variation, unknown, ApproveVariationVariables>({
    mutationFn: ({ contractId, itemId }) =>
      trackingApi.approve(contractId, "variation", itemId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: trackingKeys.item(vars.contractId, "variation", vars.itemId),
      });
      invalidateForContractType(queryClient, vars.contractId, "variation");
    },
  });
}

interface RejectVariationVariables {
  contractId: string;
  itemId: string;
  body: RejectVariationInput;
}

export function useRejectVariation() {
  const queryClient = useQueryClient();

  return useMutation<Variation, unknown, RejectVariationVariables>({
    mutationFn: ({ contractId, itemId, body }) =>
      trackingApi.reject(contractId, "variation", itemId, body),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: trackingKeys.item(vars.contractId, "variation", vars.itemId),
      });
      invalidateForContractType(queryClient, vars.contractId, "variation");
    },
  });
}

interface CorrectSowOriginalValueVariables {
  contractId: string;
  itemId: string;
  body: CorrectOriginalValueInput;
}

export function useCorrectSowOriginalValue() {
  const queryClient = useQueryClient();

  return useMutation<Sow, unknown, CorrectSowOriginalValueVariables>({
    mutationFn: ({ contractId, itemId, body }) =>
      trackingApi.correctSowOriginalValue(contractId, "sow", itemId, body),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: trackingKeys.item(vars.contractId, "sow", vars.itemId),
      });
      invalidateForContractType(queryClient, vars.contractId, "sow");
    },
  });
}

interface CorrectSowStatusVariables {
  contractId: string;
  itemId: string;
  body: CorrectStatusInput;
}

export function useCorrectSowStatus() {
  const queryClient = useQueryClient();

  return useMutation<Sow, unknown, CorrectSowStatusVariables>({
    mutationFn: ({ contractId, itemId, body }) =>
      trackingApi.correctSowStatus(contractId, "sow", itemId, body),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: trackingKeys.item(vars.contractId, "sow", vars.itemId),
      });
      invalidateForContractType(queryClient, vars.contractId, "sow");
    },
  });
}

interface CorrectVariationVariables {
  contractId: string;
  itemId: string;
  body: CorrectVariationInput;
}

export function useCorrectVariation() {
  const queryClient = useQueryClient();

  return useMutation<Variation, unknown, CorrectVariationVariables>({
    mutationFn: ({ contractId, itemId, body }) =>
      trackingApi.correctVariation(contractId, "variation", itemId, body),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: trackingKeys.item(vars.contractId, "variation", vars.itemId),
      });
      invalidateForContractType(queryClient, vars.contractId, "variation");
    },
  });
}

export function useBulkCreateKpiPeriods() {
  const queryClient = useQueryClient();

  return useMutation<KpiBatchResponse, unknown, BulkCreateKpiPeriodsVariables>({
    mutationFn: ({ contractId, items }) =>
      trackingApi.kpiPeriodBatch(contractId, { items }),
    onSuccess: (data, vars) => {
      const anySucceeded = data.items.some((r) => r.ok);
      if (!anySucceeded) return;
      // Measurements + reports both touched; invalidate the KPI parent
      // list, the two child lists, and the count badges.
      // Activity is invalidated only on the first call to avoid three
      // redundant cache-busts for the same feed.
      invalidateForContractType(queryClient, vars.contractId, "kpi");
      invalidateForContractType(queryClient, vars.contractId, "kpi-measurement", {
        invalidateActivity: false,
      });
      invalidateForContractType(queryClient, vars.contractId, "kpi-report", {
        invalidateActivity: false,
      });
    },
  });
}

// ─── Issue hooks ───────────────────────────────────────────────────────
// Issue list / item / counts reuse the generic tracking hooks
// (`useTrackingItems('issue')` etc.). The hooks below cover the issue-
// specific command surface: the chronological thread, appending entries
// (comment or action), status transitions, marking actions done, and
// closing. Every mutation returns the updated Issue (with its embedded
// `threadEntries` + denormalised `actions`), so on success we seed the
// item cache from the response AND invalidate the item / list / counts /
// summary so the register, drawer, and tab badge all refresh.

/** Invalidate all issue-scoped caches for a contract after a command. */
function invalidateIssue(
  queryClient: ReturnType<typeof useQueryClient>,
  contractId: string,
  itemId: string,
) {
  queryClient.invalidateQueries({
    queryKey: trackingKeys.item(contractId, "issue", itemId),
  });
  invalidateForContractType(queryClient, contractId, "issue");
}

/**
 * The chronological thread for an issue. The issue item GET bundles the
 * thread (getIssueWithThread), so this hook reads `threadEntries` off the
 * cached issue item rather than hitting a second endpoint — keeping the
 * thread and the item header in lockstep from a single fetch. Returns the
 * entries oldest-first (the order the backend stores them); the thread UI
 * renders them top-to-bottom as a timeline.
 */
export function useIssueThread(
  contractId: string,
  issueId: string,
): { data: IssueThreadEntry[]; isPending: boolean; error: unknown } {
  const query = useTrackingItem(contractId, "issue", issueId);
  const issue =
    query.data && query.data.type === "issue"
      ? (query.data as Issue)
      : undefined;
  return {
    data: issue?.threadEntries ?? [],
    isPending: query.isPending,
    error: query.error,
  };
}

/** Discriminated append: a free-text comment OR a committed action. The
 *  composer's Comment / Action tabs dispatch the matching variant; both hit
 *  their dedicated route and return the updated issue. */
export type AppendThreadEntryVariables =
  | {
      contractId: string;
      itemId: string;
      kind: "comment";
      input: AppendThreadCommentInput;
    }
  | {
      contractId: string;
      itemId: string;
      kind: "action";
      input: AppendThreadActionInput;
    };

export function useAppendThreadEntry() {
  const queryClient = useQueryClient();

  return useMutation<Issue, unknown, AppendThreadEntryVariables>({
    mutationFn: (vars) =>
      vars.kind === "comment"
        ? trackingApi.appendIssueComment(vars.contractId, vars.itemId, vars.input)
        : trackingApi.appendIssueAction(vars.contractId, vars.itemId, vars.input),
    onSuccess: (issue, vars) => {
      queryClient.setQueryData(
        trackingKeys.item(vars.contractId, "issue", vars.itemId),
        issue,
      );
      invalidateIssue(queryClient, vars.contractId, vars.itemId);
    },
  });
}

interface TransitionIssueVariables {
  contractId: string;
  itemId: string;
  body: TransitionIssueInput;
}

/** Status transition — handles every non-close status change, including
 *  →resolved and resolved→open re-open. Resolving is a transition with
 *  `to: 'resolved'`; there is no separate resolve hook. */
export function useTransitionIssue() {
  const queryClient = useQueryClient();

  return useMutation<Issue, unknown, TransitionIssueVariables>({
    mutationFn: ({ contractId, itemId, body }) =>
      trackingApi.transitionIssue(contractId, itemId, body),
    onSuccess: (issue, vars) => {
      queryClient.setQueryData(
        trackingKeys.item(vars.contractId, "issue", vars.itemId),
        issue,
      );
      invalidateIssue(queryClient, vars.contractId, vars.itemId);
    },
  });
}

interface CloseIssueVariables {
  contractId: string;
  itemId: string;
  body: CloseIssueInput;
}

export function useCloseIssue() {
  const queryClient = useQueryClient();

  return useMutation<Issue, unknown, CloseIssueVariables>({
    mutationFn: ({ contractId, itemId, body }) =>
      trackingApi.closeIssue(contractId, itemId, body),
    onSuccess: (issue, vars) => {
      queryClient.setQueryData(
        trackingKeys.item(vars.contractId, "issue", vars.itemId),
        issue,
      );
      invalidateIssue(queryClient, vars.contractId, vars.itemId);
    },
  });
}

interface MarkActionDoneVariables {
  contractId: string;
  itemId: string;
  /** The raising action_raised thread entry id. */
  entryId: string;
  body: MarkActionDoneInput;
}

export function useMarkActionDone() {
  const queryClient = useQueryClient();

  return useMutation<Issue, unknown, MarkActionDoneVariables>({
    mutationFn: ({ contractId, itemId, entryId, body }) =>
      trackingApi.markIssueActionDone(contractId, itemId, entryId, body),
    onSuccess: (issue, vars) => {
      queryClient.setQueryData(
        trackingKeys.item(vars.contractId, "issue", vars.itemId),
        issue,
      );
      invalidateIssue(queryClient, vars.contractId, vars.itemId);
    },
  });
}
